import React, { useState, useEffect } from "react";
import ImageWithBasePath from "../../core/common/imageWithBasePath";
import Breadcrumb from "../../core/common/Breadcrumb/breadcrumb";
import { useTranslation } from "react-i18next";

const APPOINTMENT_FEE = 750;

type Appointment = {
  id: string | number;
  appointmentTopic?: string;
  appointmentTime?: string;
  status?: string;
  meetingLink?: string;
  questions?: string;
  assignedTo?: { name: string };
};

const BookAppointment = () => {
  const { t } = useTranslation();
  const steps: string[] = t("appointment.steps", { returnObjects: true }) as string[];

  const [showModal, setShowModal] = useState(false);
  const [modalStep, setModalStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [userAppointments, setUserAppointments] = useState<Appointment[]>([]);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showTimeChangeModal, setShowTimeChangeModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successDetails, setSuccessDetails] = useState({
    title: "",
    message: "",
    type: "",
  });

  const [selectedAppointment, setSelectedAppointment] =
    useState<Appointment | null>(null);

  const [form, setForm] = useState({
    userName: "",
    userEmail: "",
    userPhone: "",
    appointmentTopic: "",
    questions: "",
  });

  const [paymentSlipFile, setPaymentSlipFile] = useState<File | null>(null);
  const [paymentMethod, setPaymentMethod] = useState("bkash");
  const [paymentDetails, setPaymentDetails] = useState({
    amount: String(APPOINTMENT_FEE),
    transactionId: "",
    bankName: "",
    branchName: "",
    referenceNumber: "",
    paymentDate: "",
  });

  const [timeChangeForm, setTimeChangeForm] = useState({
    newRequestedTime: "",
    reason: "",
  });

  // Check for existing appointments when email is entered
  useEffect(() => {
    if (form.userEmail && form.userEmail.includes("@")) {
      checkUserAppointments(form.userEmail);
    }
  }, [form.userEmail]);

  const checkUserAppointments = async (email: string | number | boolean) => {
    try {
      console.log("Checking appointments for:", email);

      const res = await fetch(
        `${
          process.env.REACT_APP_API_BASE_URL
        }/api/appointments/my-appointments?email=${encodeURIComponent(email)}`,
      );

      if (res.ok) {
        const appointments = await res.json();
        console.log("Raw API response:", appointments);

        // Check each appointment
        appointments.forEach((apt: any, index: number) => {
          console.log(`Appointment ${index}:`, {
            id: apt.id,
            topic: apt.appointmentTopic,
            status: apt.status,
            time: apt.appointmentTime,
          });
        });

        setUserAppointments(appointments);
      } else {
        console.error("API call failed:", res.status, res.statusText);
      }
    } catch (err) {
      console.error("Error checking appointments:", err);
    }
  };

  const showSuccessConfirmation = (
    title: string,
    message: string,
    type: string = "success",
  ) => {
    setSuccessDetails({ title, message, type });
    setShowSuccessModal(true);
  };

  const handleChange = (e: { target: { name: any; value: any } }) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handlePaymentDetailsChange = (e: {
    target: { name: any; value: any };
  }) => {
    setPaymentDetails((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleTimeChangeFormChange = (e: {
    target: { name: any; value: any };
  }) => {
    setTimeChangeForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleFormSubmit = async (e: { preventDefault: () => void }) => {
    e.preventDefault();
    setError(null);
    if (
      !form.userName ||
      !form.userEmail ||
      !form.userPhone ||
      !form.appointmentTopic
    ) {
      setError("Please fill in all required fields.");
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/appointments/check-appointment`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: form.userEmail }),
        },
      );
      if (!res.ok) {
        const data = await res.json();
        setError(data?.message || "Something went wrong. Please try again.");
        setLoading(false);
        return;
      }
      setModalStep(2);
    } catch (err) {
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentDetailsSubmit = async (e: {
    preventDefault: () => void;
  }) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    if (!paymentSlipFile) {
      setError("Please upload your payment slip.");
      setLoading(false);
      return;
    }

    const appointmentData = {
      ...form,
      appointmentTopic: form.appointmentTopic.toUpperCase(),
      questions: form.questions,
      notes: "",
      payment: {
        paymentMethod,
        amount: paymentDetails.amount,
        transactionId: paymentDetails.transactionId,
        bankName: paymentDetails.bankName,
        branchName: paymentDetails.branchName,
        referenceNumber: paymentDetails.referenceNumber,
        paymentDate: paymentDetails.paymentDate || null,
      },
    };

    try {
      const formData = new FormData();
      formData.append(
        "appointment",
        new Blob([JSON.stringify(appointmentData)], {
          type: "application/json",
        }),
      );
      formData.append("paymentSlip", paymentSlipFile);

      const res = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/appointments`,
        {
          method: "POST",
          body: formData,
        },
      );

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.error || "Failed to submit appointment");
      }

      setModalStep(4);
    } catch (err) {
      setError(
        err && typeof err === "object" && "message" in err
          ? (err as { message?: string }).message || "Something went wrong."
          : "Something went wrong.",
      );
    } finally {
      setLoading(false);
    }
  };

  // Confirm appointment - Updated with success confirmation
  const handleConfirmAppointment = async (appointmentId: any) => {
    setLoading(true);
    try {
      const res = await fetch(
        `${
          process.env.REACT_APP_API_BASE_URL
        }/api/appointments/${appointmentId}/confirm?userEmail=${encodeURIComponent(
          form.userEmail,
        )}`,
        { method: "PUT" },
      );

      const data = await res.json();
      if (res.ok) {
        await checkUserAppointments(form.userEmail); // Refresh appointments

        showSuccessConfirmation(
          "Appointment Confirmed!",
          "Your appointment has been successfully confirmed. We'll send you a meeting reminder before the scheduled time. Please join the meeting using the link provided.",
          "confirmed",
        );
      } else {
        setError(data.message || "Failed to confirm appointment");
        setTimeout(() => setError(""), 5000);
      }
    } catch (err) {
      setError("Network error. Please try again.");
      setTimeout(() => setError(""), 5000);
    } finally {
      setLoading(false);
    }
  };

  // Open time change modal
  const openTimeChangeModal = (appointment: Appointment) => {
    setSelectedAppointment(appointment);
    setTimeChangeForm({
      newRequestedTime: "",
      reason: "",
    });
    setShowTimeChangeModal(true);
  };

  // Handle time change request - Updated with success confirmation
  const handleTimeChangeSubmit = async (e: { preventDefault: () => void }) => {
    e.preventDefault();
    if (!timeChangeForm.newRequestedTime) {
      setError("Please select a new date and time");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const newDateTime = new Date(
        timeChangeForm.newRequestedTime,
      ).toISOString();

      if (!selectedAppointment) {
        setError("No appointment selected for time change.");
        setLoading(false);
        return;
      }

      const res = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/appointments/${
          selectedAppointment.id
        }/request-time-change?userEmail=${encodeURIComponent(form.userEmail)}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            newRequestedTime: newDateTime,
            reason: timeChangeForm.reason || "User requested time change",
          }),
        },
      );

      const data = await res.json();
      if (res.ok) {
        setShowTimeChangeModal(false);
        await checkUserAppointments(form.userEmail); // Refresh appointments

        const requestedTime = new Date(
          timeChangeForm.newRequestedTime,
        ).toLocaleString();
        showSuccessConfirmation(
          "Time Change Requested!",
          `Your request to change the appointment time to ${requestedTime} has been submitted successfully. Our team will review your request and contact you with confirmation shortly.`,
          "time-change",
        );
      } else {
        setError(data.message || "Failed to request time change");
      }
    } catch (err) {
      setError("Invalid date format or network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: any) => {
    switch (status) {
      case "PENDING_PAYMENT":
        return "bg-orange-100 text-orange-800";
      case "PENDING":
        return "bg-yellow-100 text-yellow-800";
      case "INVITED":
        return "bg-blue-100 text-blue-800";
      case "CONFIRMED":
        return "bg-green-100 text-green-800";
      case "COMPLETED":
        return "bg-gray-100 text-gray-800";
      case "CANCELLED":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const canModifyAppointment = (appointment: Appointment) => {
    return (
      appointment.status !== "CONFIRMED" &&
      appointment.status !== "COMPLETED" &&
      appointment.status !== "CANCELLED"
    );
  };

  const closeModal = () => {
    setShowModal(false);
    setModalStep(1);
    setForm({
      userName: "",
      userEmail: "",
      userPhone: "",
      appointmentTopic: "",
      questions: "",
    });
    setPaymentMethod("bkash");
    setPaymentDetails({
      amount: String(APPOINTMENT_FEE),
      transactionId: "",
      bankName: "",
      branchName: "",
      referenceNumber: "",
      paymentDate: "",
    });
    setPaymentSlipFile(null);
    setError(null);
    setLoading(false);
    setUserAppointments([]);
  };

  const closeTimeChangeModal = () => {
    setShowTimeChangeModal(false);
    setSelectedAppointment(null);
    setTimeChangeForm({ newRequestedTime: "", reason: "" });
    setError(null);
  };

  const closeSuccessModal = () => {
    setShowSuccessModal(false);
    setSuccessDetails({ title: "", message: "", type: "" });
  };

  return (
    <>
      <Breadcrumb title={t("appointment.breadcrumb")} />

      {/* Global Error Messages */}
      {error &&
        !showModal &&
        !showStatusModal &&
        !showTimeChangeModal &&
        !showSuccessModal && (
          <div className="fixed top-4 right-4 z-[9998] bg-red-500 text-white px-6 py-3 rounded-lg shadow-lg">
            {error}
          </div>
        )}

      <section className="bg-gray-50 py-10">
        <div className="container mx-auto px-4">
          <div className="row">
            {/* Left Column */}
            <div className="col-lg-8">
              <ImageWithBasePath
                src="assets/img/pages/book-an-appointment.png"
                alt="Book an Appointment"
                className="img-fluid rounded mb-4"
              />

              <div className="bg-white p-5 shadow rounded mb-4">
                <h2 className="text-2xl font-semibold mb-4 bg-[#009fe3] text-white px-3 py-2 rounded">
                  {t("appointment.whyTitle")}
                </h2>
                <p className="text-gray-600 leading-relaxed">
                  {t("appointment.whyBody")}
                </p>
              </div>

              <div className="bg-white p-5 shadow rounded">
                <h2 className="text-2xl font-semibold mb-4 bg-[#009fe3] text-white px-3 py-2 rounded">
                  {t("appointment.howTitle")}
                </h2>
                <ul className="list-decimal pl-5 space-y-3 text-gray-600">
                  {steps.map((step, index) => (
                    <li key={index} className="leading-relaxed">
                      {step}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Right Column */}
            <div className="col-lg-4">
              <div className="position-sticky top-20">
                <div className="bg-white p-5 shadow rounded">
                  <h2 className="text-xl font-semibold mb-4 bg-[#009fe3] text-white px-3 py-2 rounded">
                    {t("appointment.priceTitle")}
                  </h2>
                  <p className="text-gray-600 mb-3">
                    <strong>{t("appointment.fee")}</strong> {t("appointment.feeValue")}
                  </p>
                  <p className="text-gray-600 mb-4">
                    <strong>{t("appointment.duration")}</strong> {t("appointment.durationValue")}
                  </p>

                  <button
                    onClick={() => setShowModal(true)}
                    className="w-full inline-block bg-[#009fe3] text-white font-medium py-3 px-6 rounded hover:bg-[#007bbf] transition duration-300 text-center"
                  >
                    {t("appointment.bookNow")}
                  </button>

                  <button
                    onClick={() => setShowStatusModal(true)}
                    className="w-full mt-3 inline-block bg-gray-500 text-white font-medium py-3 px-6 rounded hover:bg-gray-600 transition duration-300 text-center"
                  >
                    {t("appointment.checkStatus")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Booking Modal */}
      {showModal && (
        <div className="fixed inset-0 z-[9999] bg-black/60 flex items-center justify-center p-3">
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[92vh] overflow-y-auto z-[10000]">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-[#009fe3] to-[#0077b6] px-6 pt-4 pb-3 rounded-t-2xl">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-base font-bold text-white">
                  {t("appointment.modal.title")}
                </h2>
                <button
                  onClick={closeModal}
                  className="text-white/80 hover:text-white text-2xl leading-none"
                  aria-label="Close"
                >
                  &times;
                </button>
              </div>

              {/* Step indicator */}
              {modalStep < 4 && (
                <div className="flex items-center">
                  {[
                    { n: 1, label: t("appointment.modal.step1") },
                    { n: 2, label: t("appointment.modal.step2") },
                    { n: 3, label: t("appointment.modal.step3") },
                  ].map(({ n, label }, i) => (
                    <React.Fragment key={n}>
                      <div className="flex items-center gap-1.5">
                        <div
                          className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold border-2 shrink-0 ${
                            modalStep > n
                              ? "bg-white border-white text-[#009fe3]"
                              : modalStep === n
                                ? "bg-white border-white text-[#009fe3]"
                                : "bg-transparent border-white/40 text-white/50"
                          }`}
                        >
                          {modalStep > n ? "✓" : n}
                        </div>
                        <span
                          className={`text-xs font-medium hidden sm:inline ${modalStep >= n ? "text-white" : "text-white/40"}`}
                        >
                          {label}
                        </span>
                      </div>
                      {i < 2 && (
                        <div
                          className={`flex-1 h-0.5 mx-2 ${modalStep > n ? "bg-white" : "bg-white/30"}`}
                        />
                      )}
                    </React.Fragment>
                  ))}
                </div>
              )}
            </div>

            {/* Modal Body */}
            <div className="px-6 py-6">
              {/* ── Step 1: Your Info ── */}
              {modalStep === 1 && (
                <>
                  {userAppointments.length > 0 && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-5">
                      <p className="text-sm text-amber-800 font-medium mb-2">
                        {t("appointment.modal.existingWarning")}
                      </p>
                      {userAppointments.map((apt) => (
                        <div
                          key={apt.id}
                          className="text-xs text-amber-700 flex items-center gap-2 mt-1"
                        >
                          <span>{t("appointment.modal.statusLabel")}</span>
                          <span
                            className={`px-2 py-0.5 rounded-full ${getStatusColor(apt.status)}`}
                          >
                            {apt.status}
                          </span>
                          {apt.appointmentTime && (
                            <span>
                              {new Date(apt.appointmentTime).toLocaleString()}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  <form onSubmit={handleFormSubmit} className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">
                          {t("appointment.modal.fullName")} <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="userName"
                          value={form.userName}
                          onChange={handleChange}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#009fe3] focus:border-transparent"
                          placeholder={t("appointment.modal.namePlaceholder")}
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">
                          {t("appointment.modal.emailAddress")} <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="email"
                          name="userEmail"
                          value={form.userEmail}
                          onChange={handleChange}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#009fe3] focus:border-transparent"
                          placeholder={t("appointment.modal.emailPlaceholder")}
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">
                          {t("appointment.modal.phoneNumber")} <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="tel"
                          name="userPhone"
                          value={form.userPhone}
                          onChange={handleChange}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#009fe3] focus:border-transparent"
                          placeholder={t("appointment.modal.phonePlaceholder")}
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">
                          {t("appointment.modal.topic")} <span className="text-red-500">*</span>
                        </label>
                        <select
                          name="appointmentTopic"
                          value={form.appointmentTopic}
                          onChange={handleChange}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#009fe3] focus:border-transparent"
                          required
                        >
                          <option value="" disabled>
                            {t("appointment.modal.topicPlaceholder")}
                          </option>
                          <option value="GERMAN_LANGUAGE">{t("appointment.modal.topicGerman")}</option>
                          <option value="AUSBILDUNG">{t("appointment.modal.topicAusbildung")}</option>
                          <option value="STUDY">{t("appointment.modal.topicStudy")}</option>
                          <option value="FSJ_BFD">{t("appointment.modal.topicFsj")}</option>
                          <option value="AU_PAIR">{t("appointment.modal.topicAuPair")}</option>
                          <option value="GERMAN_LANGUAGE_VISA">{t("appointment.modal.topicGermanVisa")}</option>
                          <option value="OTHERS">{t("appointment.modal.topicOthers")}</option>
                        </select>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">
                        {t("appointment.modal.questions")}
                      </label>
                      <textarea
                        name="questions"
                        value={form.questions}
                        onChange={handleChange}
                        rows={3}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#009fe3] focus:border-transparent resize-none"
                        placeholder={t("appointment.modal.questionsPlaceholder")}
                      />
                    </div>
                    {error && (
                      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-2.5 rounded-lg text-sm">
                        {error}
                      </div>
                    )}
                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full bg-[#009fe3] hover:bg-[#0077b6] text-white font-semibold py-3 rounded-lg transition-colors disabled:opacity-60"
                    >
                      {loading ? t("appointment.modal.checking") : t("appointment.modal.continuePayment")}
                    </button>
                  </form>
                </>
              )}

              {/* ── Step 2: Payment Method ── */}
              {modalStep === 2 && (
                <>
                  <p className="text-sm text-gray-500 mb-5">
                    {t("appointment.modal.paymentSubtitle")}
                  </p>
                  <div className="flex flex-col gap-3 mb-6">
                    <label
                      className={`flex items-center gap-4 p-4 border-2 rounded-xl cursor-pointer transition-colors ${
                        paymentMethod === "bkash"
                          ? "border-[#009fe3] bg-blue-50"
                          : "border-gray-200 hover:border-gray-300"
                      }`}
                    >
                      <input
                        type="radio"
                        value="bkash"
                        checked={paymentMethod === "bkash"}
                        onChange={() => setPaymentMethod("bkash")}
                        className="accent-[#009fe3]"
                      />
                      <div className="flex-1">
                        <div className="font-semibold text-gray-800">{t("appointment.modal.bkash")}</div>
                        <div className="text-sm text-gray-500">
                          {t("appointment.modal.bkashDesc")}
                        </div>
                      </div>
                      <span className="text-[#009fe3] font-bold text-sm">
                        {APPOINTMENT_FEE} BDT
                      </span>
                    </label>

                    <label
                      className={`flex items-center gap-4 p-4 border-2 rounded-xl cursor-pointer transition-colors ${
                        paymentMethod === "bank"
                          ? "border-[#009fe3] bg-blue-50"
                          : "border-gray-200 hover:border-gray-300"
                      }`}
                    >
                      <input
                        type="radio"
                        value="bank"
                        checked={paymentMethod === "bank"}
                        onChange={() => setPaymentMethod("bank")}
                        className="accent-[#009fe3]"
                      />
                      <div className="flex-1">
                        <div className="font-semibold text-gray-800">
                          {t("appointment.modal.bankTransfer")}
                        </div>
                        <div className="text-sm text-gray-500">
                          {t("appointment.modal.bankDesc")}
                        </div>
                      </div>
                      <span className="text-[#009fe3] font-bold text-sm">
                        {APPOINTMENT_FEE} BDT
                      </span>
                    </label>
                  </div>

                  <button
                    className="w-full bg-[#009fe3] hover:bg-[#0077b6] text-white font-semibold py-3 rounded-lg transition-colors"
                    onClick={() => setModalStep(3)}
                  >
                    {t("appointment.modal.continueBtn")}
                  </button>
                  <button
                    className="w-full mt-3 text-gray-500 hover:text-gray-700 text-sm py-2 transition-colors"
                    onClick={() => setModalStep(1)}
                  >
                    {t("appointment.modal.backBtn")}
                  </button>
                </>
              )}

              {/* ── Step 3: Payment Details + Slip ── */}
              {modalStep === 3 && (
                <>
                  {/* Payment instructions box */}
                  <div className="bg-[#f0f9ff] border border-[#bae6fd] rounded-xl p-4 mb-5">
                    <p className="text-sm font-semibold text-[#0369a1] mb-2">
                      {paymentMethod === "bkash"
                        ? t("appointment.modal.sendViaBkash")
                        : t("appointment.modal.bankDetails")}
                    </p>
                    {paymentMethod === "bkash" ? (
                      <div className="text-sm text-gray-700 space-y-1">
                        <p>{t("appointment.modal.sendAmountBkash")}</p>
                        <p className="text-lg font-bold text-[#009fe3] tracking-wide">
                          +8801645731253
                        </p>
                      </div>
                    ) : (
                      <div className="text-sm text-gray-700 space-y-1">
                        <p>{t("appointment.modal.sendAmountBank")}</p>
                        <div className="mt-1 space-y-0.5">
                          <p>
                            <span className="font-medium">{t("appointment.modal.accountName")}:</span>{" "}
                            MD Samsuddoha
                          </p>
                          <p>
                            <span className="font-medium">{t("appointment.modal.bank")}:</span> BRAC Bank
                          </p>
                          <p>
                            <span className="font-medium">{t("appointment.modal.branch")}:</span> Rangpur
                          </p>
                          <p>
                            <span className="font-medium">{t("appointment.modal.accountNumber")}:</span>{" "}
                            1069811730001
                          </p>
                        </div>
                      </div>
                    )}
                  </div>

                  <form
                    onSubmit={handlePaymentDetailsSubmit}
                    className="space-y-3"
                  >
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">
                        {t("appointment.modal.amountPaid")}{" "}
                        <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="amount"
                        value={paymentDetails.amount}
                        onChange={handlePaymentDetailsChange}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#009fe3] focus:border-transparent"
                        required
                      />
                    </div>

                    {paymentMethod === "bkash" ? (
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">
                          {t("appointment.modal.bkashTxnId")}{" "}
                          <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="transactionId"
                          value={paymentDetails.transactionId}
                          onChange={handlePaymentDetailsChange}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#009fe3] focus:border-transparent"
                          placeholder={t("appointment.modal.bkashTxnPlaceholder")}
                          required
                        />
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-1">
                            {t("appointment.modal.bankName")} <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            name="bankName"
                            value={paymentDetails.bankName}
                            onChange={handlePaymentDetailsChange}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#009fe3] focus:border-transparent"
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-1">
                            {t("appointment.modal.branchName")}
                          </label>
                          <input
                            type="text"
                            name="branchName"
                            value={paymentDetails.branchName}
                            onChange={handlePaymentDetailsChange}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#009fe3] focus:border-transparent"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-1">
                            {t("appointment.modal.referenceNumber")}{" "}
                            <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            name="referenceNumber"
                            value={paymentDetails.referenceNumber}
                            onChange={handlePaymentDetailsChange}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#009fe3] focus:border-transparent"
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-1">
                            {t("appointment.modal.paymentDate")} <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="date"
                            name="paymentDate"
                            value={paymentDetails.paymentDate}
                            onChange={handlePaymentDetailsChange}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#009fe3] focus:border-transparent"
                            required
                          />
                        </div>
                      </div>
                    )}

                    {/* File upload area */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">
                        {t("appointment.modal.paymentSlip")} <span className="text-red-500">*</span>
                        <span className="font-normal text-gray-400 ml-1">
                          {t("appointment.modal.slipNote")}
                        </span>
                        <span className="font-normal text-gray-400 ml-1">(Max 2MB)</span>
                      </label>
                      <label
                        className={`flex flex-col items-center justify-center border-2 border-dashed rounded-xl py-6 px-4 cursor-pointer transition-colors ${
                          paymentSlipFile
                            ? "border-green-400 bg-green-50"
                            : "border-gray-300 bg-gray-50 hover:border-[#009fe3] hover:bg-blue-50"
                        }`}
                      >
                        <input
                          type="file"
                          accept="image/*,application/pdf"
                          onChange={(e) =>
                            setPaymentSlipFile(e.target.files?.[0] || null)
                          }
                          className="hidden"
                          required={!paymentSlipFile}
                        />
                        {paymentSlipFile ? (
                          <>
                            <svg
                              className="w-8 h-8 text-green-500 mb-2"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M5 13l4 4L19 7"
                              />
                            </svg>
                            <p className="text-sm font-medium text-green-700">
                              {paymentSlipFile.name}
                            </p>
                            <p className="text-xs text-green-500 mt-0.5">
                              {t("appointment.modal.clickToChange")}
                            </p>
                          </>
                        ) : (
                          <>
                            <svg
                              className="w-8 h-8 text-gray-400 mb-2"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                              />
                            </svg>
                            <p className="text-sm text-gray-600 font-medium">
                              {t("appointment.modal.uploadSlip")}
                            </p>
                            <p className="text-xs text-gray-400 mt-0.5">
                              {t("appointment.modal.uploadFormats")}
                            </p>
                          </>
                        )}
                      </label>
                    </div>

                    {error && (
                      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-2.5 rounded-lg text-sm">
                        {error}
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full bg-[#009fe3] hover:bg-[#0077b6] text-white font-semibold py-3 rounded-lg transition-colors disabled:opacity-60"
                    >
                      {loading
                        ? t("appointment.modal.submitting")
                        : t("appointment.modal.submitBtn")}
                    </button>
                  </form>

                  <button
                    className="w-full mt-3 text-gray-500 hover:text-gray-700 text-sm py-2 transition-colors"
                    onClick={() => setModalStep(2)}
                  >
                    ← Back
                  </button>
                </>
              )}

              {/* ── Step 4: Success ── */}
              {modalStep === 4 && (
                <div className="py-4 text-center">
                  <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-5">
                    <svg
                      className="w-10 h-10 text-green-500"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2.5"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    {t("appointment.modal.successTitle")}
                  </h3>
                  <p className="text-gray-500 text-sm mb-6 leading-relaxed">
                    {t("appointment.modal.successBody")}
                    <br />
                    {t("appointment.modal.successBody2")}
                  </p>
                  <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 mb-6 text-left">
                    <p className="text-sm text-orange-800 font-semibold mb-1">
                      {t("appointment.modal.whatsNext")}
                    </p>
                    <ol className="text-xs text-orange-700 space-y-1 list-decimal pl-4">
                      <li>{t("appointment.modal.nextStep1")}</li>
                      <li>{t("appointment.modal.nextStep2")}</li>
                      <li>{t("appointment.modal.nextStep3")}</li>
                    </ol>
                  </div>
                  <button
                    onClick={closeModal}
                    className="w-full bg-[#009fe3] hover:bg-[#0077b6] text-white font-semibold py-3 rounded-lg transition-colors"
                  >
                    {t("appointment.modal.done")}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Status Check Modal */}
      {showStatusModal && (
        <div className="fixed inset-0 z-[9999] bg-black/60 flex items-center justify-center p-3">
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-xl max-h-[92vh] overflow-y-auto z-[10000]">
            {/* Header */}
            <div className="bg-gradient-to-r from-[#009fe3] to-[#0077b6] px-6 pt-4 pb-4 rounded-t-2xl flex items-center justify-between">
              <div>
                <h2 className="text-base font-bold text-white">
                  {t("appointment.status.title")}
                </h2>
                <p className="text-white/75 text-xs mt-0.5">
                  {t("appointment.status.subtitle")}
                </p>
              </div>
              <button
                onClick={() => setShowStatusModal(false)}
                className="text-white/80 hover:text-white text-2xl leading-none ml-4"
              >
                &times;
              </button>
            </div>

            <div className="px-6 py-5">
              {/* Email input */}
              <div className="flex gap-2 mb-5">
                <input
                  type="email"
                  value={form.userEmail}
                  onChange={(e) =>
                    setForm({ ...form, userEmail: e.target.value })
                  }
                  onKeyDown={(e) =>
                    e.key === "Enter" &&
                    form.userEmail &&
                    checkUserAppointments(form.userEmail)
                  }
                  className="flex-1 border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#009fe3] focus:border-transparent"
                  placeholder="your@email.com"
                />
                <button
                  onClick={() =>
                    form.userEmail && checkUserAppointments(form.userEmail)
                  }
                  disabled={!form.userEmail || loading}
                  className="bg-[#009fe3] hover:bg-[#0077b6] text-white px-4 py-2.5 rounded-lg text-sm font-semibold transition-colors disabled:opacity-50 whitespace-nowrap"
                >
                  {loading ? "..." : t("appointment.status.check")}
                </button>
              </div>

              {/* Results */}
              {userAppointments.length > 0 ? (
                <div className="space-y-4">
                  {userAppointments.map((appointment) => {
                    const statusMeta: Record<
                      string,
                      {
                        icon: string;
                        bg: string;
                        border: string;
                        title: string;
                        message: string;
                      }
                    > = {
                      PENDING_PAYMENT: {
                        icon: "💳",
                        bg: "bg-orange-50",
                        border: "border-orange-200",
                        title: t("appointment.status.PENDING_PAYMENT.title"),
                        message: t("appointment.status.PENDING_PAYMENT.message"),
                      },
                      PENDING: {
                        icon: "⏳",
                        bg: "bg-yellow-50",
                        border: "border-yellow-200",
                        title: t("appointment.status.PENDING.title"),
                        message: t("appointment.status.PENDING.message"),
                      },
                      INVITED: {
                        icon: "📩",
                        bg: "bg-blue-50",
                        border: "border-blue-200",
                        title: t("appointment.status.INVITED.title"),
                        message: t("appointment.status.INVITED.message"),
                      },
                      CONFIRMED: {
                        icon: "✅",
                        bg: "bg-green-50",
                        border: "border-green-200",
                        title: t("appointment.status.CONFIRMED.title"),
                        message: t("appointment.status.CONFIRMED.message"),
                      },
                      COMPLETED: {
                        icon: "🎉",
                        bg: "bg-gray-50",
                        border: "border-gray-200",
                        title: t("appointment.status.COMPLETED.title"),
                        message: t("appointment.status.COMPLETED.message"),
                      },
                      CANCELLED: {
                        icon: "❌",
                        bg: "bg-red-50",
                        border: "border-red-200",
                        title: t("appointment.status.CANCELLED.title"),
                        message: t("appointment.status.CANCELLED.message"),
                      },
                    };
                    const meta = statusMeta[appointment.status ?? ""] ?? {
                      icon: "📋",
                      bg: "bg-gray-50",
                      border: "border-gray-200",
                      title: appointment.status ?? "Unknown",
                      message: "",
                    };

                    return (
                      <div
                        key={appointment.id}
                        className={`rounded-xl border ${meta.border} ${meta.bg} overflow-hidden`}
                      >
                        {/* Card header */}
                        <div className="flex items-center justify-between px-4 py-3 border-b border-inherit">
                          <div className="flex items-center gap-2">
                            <span className="text-lg">{meta.icon}</span>
                            <div>
                              <p className="text-sm font-semibold text-gray-900">
                                {appointment.appointmentTopic}
                              </p>
                              <p className="text-xs text-gray-500">
                                {t("appointment.status.booking")}{appointment.id}
                              </p>
                            </div>
                          </div>
                          <span
                            className={`px-2.5 py-1 rounded-full text-xs font-semibold ${getStatusColor(appointment.status)}`}
                          >
                            {appointment.status}
                          </span>
                        </div>

                        {/* Card body */}
                        <div className="px-4 py-3 space-y-2">
                          {/* Status message */}
                          <p className="text-xs text-gray-600 leading-relaxed">
                            {meta.message}
                          </p>

                          {/* Details grid */}
                          {appointment.appointmentTime && (
                            <div className="flex items-center gap-2 text-sm">
                              <span className="text-gray-400 text-xs">🕐</span>
                              <span className="font-medium text-gray-700 text-xs">
                                {new Date(
                                  appointment.appointmentTime,
                                ).toLocaleString("en-GB", {
                                  day: "2-digit",
                                  month: "short",
                                  year: "numeric",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                })}
                              </span>
                            </div>
                          )}

                          {appointment.meetingLink && (
                            <div className="flex items-center gap-2">
                              <span className="text-gray-400 text-xs">🔗</span>
                              <a
                                href={
                                  appointment.meetingLink.startsWith("http")
                                    ? appointment.meetingLink
                                    : `https://${appointment.meetingLink}`
                                }
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs font-semibold text-[#009fe3] hover:underline"
                              >
                                {t("appointment.status.joinMeeting")}
                              </a>
                            </div>
                          )}

                          {appointment.questions && (
                            <div className="text-xs text-gray-500 bg-white/60 rounded-lg px-3 py-2 mt-1">
                              <span className="font-medium text-gray-600">
                                {t("appointment.status.yourQuestions")}{" "}
                              </span>
                              {appointment.questions}
                            </div>
                          )}

                          {/* Action buttons */}
                          {(appointment.status === "INVITED" ||
                            canModifyAppointment(appointment)) && (
                            <div className="flex flex-wrap gap-2 pt-1">
                              {appointment.status === "INVITED" && (
                                <button
                                  onClick={() =>
                                    handleConfirmAppointment(appointment.id)
                                  }
                                  disabled={loading}
                                  className="bg-green-600 hover:bg-green-700 text-white px-4 py-1.5 rounded-lg text-xs font-semibold transition-colors disabled:opacity-50"
                                >
                                  {loading
                                    ? t("appointment.status.confirming")
                                    : t("appointment.status.confirmBtn")}
                                </button>
                              )}
                              {canModifyAppointment(appointment) &&
                                appointment.status !== "PENDING_PAYMENT" && (
                                  <button
                                    onClick={() =>
                                      openTimeChangeModal(appointment)
                                    }
                                    disabled={loading}
                                    className="bg-white border border-blue-300 text-blue-700 hover:bg-blue-50 px-4 py-1.5 rounded-lg text-xs font-semibold transition-colors"
                                  >
                                    {t("appointment.status.timeChangeBtn")}
                                  </button>
                                )}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : form.userEmail && form.userEmail.includes("@") ? (
                <div className="text-center py-10">
                  <div className="text-4xl mb-3">📭</div>
                  <p className="text-gray-500 text-sm font-medium">
                    {t("appointment.status.notFound")}
                  </p>
                  <p className="text-gray-400 text-xs mt-1">
                    {t("appointment.status.notFoundDesc")}
                  </p>
                </div>
              ) : (
                <div className="text-center py-10">
                  <div className="text-4xl mb-3">📧</div>
                  <p className="text-gray-400 text-sm">
                    {t("appointment.status.enterEmail")}
                  </p>
                </div>
              )}
            </div>

            <div className="px-6 pb-5">
              <button
                onClick={() => setShowStatusModal(false)}
                className="w-full border border-gray-200 text-gray-600 hover:bg-gray-50 font-medium py-2.5 rounded-lg text-sm transition-colors"
              >
                {t("appointment.status.close")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Time Change Modal */}
      {showTimeChangeModal && selectedAppointment && (
        <div className="fixed inset-0 z-[9999] bg-black/70 flex items-center justify-center">
          <div className="relative bg-white rounded shadow-lg w-full max-w-lg mx-2 sm:mx-auto p-6 z-[10000]">
            <button
              onClick={closeTimeChangeModal}
              className="absolute top-2 right-2 text-gray-500 hover:text-gray-700 text-2xl"
            >
              &times;
            </button>

            <h2 className="text-xl font-semibold mb-4 bg-[#009fe3] text-white px-3 py-2 rounded">
              {t("appointment.timeChange.title")}
            </h2>

            <div className="mb-4 p-3 bg-blue-50 rounded">
              <p className="text-sm text-blue-800">
                <strong>{t("appointment.timeChange.currentAppointment")}</strong>{" "}
                {selectedAppointment.appointmentTopic}
              </p>
              {selectedAppointment.appointmentTime && (
                <p className="text-sm text-blue-700">
                  <strong>{t("appointment.timeChange.currentTime")}</strong>{" "}
                  {new Date(
                    selectedAppointment.appointmentTime,
                  ).toLocaleString()}
                </p>
              )}
              <p className="text-sm text-blue-700 mt-1">
                <strong>{t("appointment.timeChange.statusLabel")}</strong> {selectedAppointment.status}
              </p>
            </div>

            <form onSubmit={handleTimeChangeSubmit} className="space-y-4">
              <div>
                <label className="block mb-2 font-medium text-gray-700">
                  {t("appointment.timeChange.newTime")}
                </label>
                <input
                  type="datetime-local"
                  name="newRequestedTime"
                  value={timeChangeForm.newRequestedTime}
                  onChange={handleTimeChangeFormChange}
                  className="w-full border border-gray-300 rounded p-3 focus:ring-2 focus:ring-[#009fe3] focus:border-transparent"
                  required
                  min={new Date(Date.now() + 24 * 60 * 60 * 1000)
                    .toISOString()
                    .slice(0, 16)}
                />
              </div>

              <div>
                <label className="block mb-2 font-medium text-gray-700">
                  {t("appointment.timeChange.reason")}
                </label>
                <textarea
                  name="reason"
                  value={timeChangeForm.reason}
                  onChange={handleTimeChangeFormChange}
                  rows={3}
                  className="w-full border border-gray-300 rounded p-3 focus:ring-2 focus:ring-[#009fe3] focus:border-transparent"
                  placeholder={t("appointment.timeChange.reasonPlaceholder")}
                />
              </div>

              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-3">
                <div className="text-sm text-yellow-800">
                  {t("appointment.timeChange.note")}
                </div>
              </div>

              {error && (
                <div className="bg-red-100 border border-red-300 text-red-700 px-4 py-2 rounded">
                  {error}
                </div>
              )}

              <div className="flex gap-3 pt-2">
                <button
                  type="submit"
                  className="flex-1 bg-[#009fe3] text-white font-medium py-3 rounded hover:bg-[#007bbf] transition duration-300"
                  disabled={loading}
                >
                  {loading
                    ? t("appointment.timeChange.submitting")
                    : t("appointment.timeChange.submitBtn")}
                </button>
                <button
                  type="button"
                  onClick={closeTimeChangeModal}
                  className="flex-1 bg-gray-300 text-gray-700 font-medium py-3 rounded hover:bg-gray-400 transition duration-300"
                  disabled={loading}
                >
                  {t("appointment.timeChange.cancel")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Success Confirmation Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 z-[10001] bg-black/70 flex items-center justify-center">
          <div className="relative bg-white rounded shadow-lg w-full max-w-md mx-2 sm:mx-auto p-6 z-[10002]">
            <button
              onClick={closeSuccessModal}
              className="absolute top-2 right-2 text-gray-500 hover:text-gray-700 text-2xl"
            >
              &times;
            </button>

            <div className="text-center">
              {successDetails.type === "confirmed" && (
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg
                    className="w-8 h-8 text-green-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M5 13l4 4L19 7"
                    ></path>
                  </svg>
                </div>
              )}

              {successDetails.type === "time-change" && (
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg
                    className="w-8 h-8 text-blue-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                    ></path>
                  </svg>
                </div>
              )}

              <h3 className="text-xl font-semibold mb-4 text-gray-900">
                {successDetails.title}
              </h3>

              <p className="text-gray-600 mb-6 leading-relaxed">
                {successDetails.message}
              </p>

              <button
                onClick={closeSuccessModal}
                className="w-full bg-[#009fe3] text-white font-medium py-3 rounded hover:bg-[#007bbf] transition duration-300"
              >
                {t("appointment.success.gotIt")}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default BookAppointment;
